import {
  captureLogRocketSessionUrlWithRetry,
  identifyUser,
  startFreshLogRocketSession,
} from "../../Common/utils/logrocket";
import { saveWebSignupLogRocketSession } from "../api/logrocket";

export const WEB_LOG_RECORD_QUERY_PARAM = "logrecord";
const WEB_LOG_RECORD_SESSION_KEY = "wl_web_logrecord";
// Persists the pending replay save across the Stripe confirmPayment redirect so
// the brand-new user's session is written from the stable landing page rather
// than racing the page teardown right after create.
const WEB_LOG_RECORD_PENDING_SAVE_KEY = "wl_web_logrecord_pending_save";

let isWebLogRecordActive = false;
let lastIdentifiedWebSignupUserId: string | null = null;
let hasStartedSessionForCurrentAttempt = false;
let isFlushingWebSignupLogRocketSave = false;

const buildSignupDisplayName = (
  name?: string,
  email?: string,
  firstName?: string,
  lastName?: string,
): string => {
  const trimmedName = name?.trim();
  if (trimmedName) return trimmedName;

  const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  if (fullName) return fullName;

  const trimmedEmail = email?.trim();
  if (trimmedEmail) return trimmedEmail.split("@")[0] || trimmedEmail;

  return "Web Signup User";
};

const getWebSignupLogRocketMetadata = () => ({
  platform: "web-signup",
  logRecord: true,
});

export const syncWebLogRecordFromQuery = (): boolean => {
  if (typeof window === "undefined") {
    return isWebLogRecordActive;
  }

  const queryEnabled =
    new URLSearchParams(window.location.search).get(
      WEB_LOG_RECORD_QUERY_PARAM,
    ) === "true";

  if (queryEnabled) {
    isWebLogRecordActive = true;
    try {
      sessionStorage.setItem(WEB_LOG_RECORD_SESSION_KEY, "true");
    } catch {
      // ignore storage failures
    }
    return true;
  }

  if (!isWebLogRecordActive) {
    try {
      isWebLogRecordActive =
        sessionStorage.getItem(WEB_LOG_RECORD_SESSION_KEY) === "true";
    } catch {
      // ignore storage failures
    }
  }

  return isWebLogRecordActive;
};

export const isWebLogRecordingEnabled = (): boolean => isWebLogRecordActive;

// A stashed pending save (written at create) means the sign-up session was
// already started + identified on a prior page and is continuing across the
// Stripe redirect. The post-payment landing page reloads the bundle and resets
// module state, so this is how we know not to start a brand-new session there.
const hasPendingWebSignupLogRocketSave = (): boolean => {
  try {
    return Boolean(sessionStorage.getItem(WEB_LOG_RECORD_PENDING_SAVE_KEY));
  } catch {
    return false;
  }
};

export const appendWebLogRecordQueryParam = (url: string): string => {
  if (!isWebLogRecordingEnabled()) return url;
  if (url.includes(`${WEB_LOG_RECORD_QUERY_PARAM}=true`)) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${WEB_LOG_RECORD_QUERY_PARAM}=true`;
};

export const preserveWebLogRecordSessionFlag = (): void => {
  if (typeof sessionStorage === "undefined") return;

  const preserved = sessionStorage.getItem(WEB_LOG_RECORD_SESSION_KEY);
  const preservedPendingSave = sessionStorage.getItem(
    WEB_LOG_RECORD_PENDING_SAVE_KEY,
  );
  sessionStorage.clear();
  if (preserved === "true") {
    sessionStorage.setItem(WEB_LOG_RECORD_SESSION_KEY, preserved);
    isWebLogRecordActive = true;
  }
  if (preservedPendingSave) {
    sessionStorage.setItem(
      WEB_LOG_RECORD_PENDING_SAVE_KEY,
      preservedPendingSave,
    );
  }
};

interface IdentifyWebSignupProspectParams {
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export const identifyWebSignupProspect = ({
  email,
  firstName,
  lastName,
  phoneNumber,
}: IdentifyWebSignupProspectParams): void => {
  if (!isWebLogRecordingEnabled()) return;

  const trimmedEmail = email.trim();
  if (!trimmedEmail) return;

  // A form submit marks the start of a fresh attempt — isolate it into its own
  // LogRocket session so it does not re-tag a previous attempt's session.
  startFreshLogRocketSession();
  hasStartedSessionForCurrentAttempt = true;
  lastIdentifiedWebSignupUserId = null;

  const displayName = buildSignupDisplayName(
    undefined,
    trimmedEmail,
    firstName,
    lastName,
  );

  identifyUser(trimmedEmail, trimmedEmail, displayName, phoneNumber, {
    ...getWebSignupLogRocketMetadata(),
    signupStatus: "in-progress",
  });
};

interface IdentifyWebSignupLogRocketUserParams {
  userId: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export const identifyWebSignupLogRocketUser = ({
  userId,
  email,
  name,
  firstName,
  lastName,
  phoneNumber,
}: IdentifyWebSignupLogRocketUserParams): void => {
  if (!isWebLogRecordingEnabled()) return;

  const trimmedUserId = userId.trim();
  if (!trimmedUserId) return;
  if (lastIdentifiedWebSignupUserId === trimmedUserId) return;

  // Express checkout skips the form-submit prospect step, so start the attempt's
  // session here. The regular flow already started it during the prospect step.
  // Skip when a pending save is stashed: that means we're re-identifying the
  // existing sign-up session on the post-payment landing page (module state was
  // reset by the page reload), and starting fresh would discard the recorded
  // sign-up flow before flushWebSignupLogRocketSave captures it.
  if (
    !hasStartedSessionForCurrentAttempt &&
    !hasPendingWebSignupLogRocketSave()
  ) {
    startFreshLogRocketSession();
  }

  const displayName = buildSignupDisplayName(name, email, firstName, lastName);
  const metadata = getWebSignupLogRocketMetadata();

  // Re-tags the live session with the Firebase userId so the LogRocket replay is
  // attributed to the created user. Persistence to Firestore is handled by the
  // backend signup endpoint via the session URL captured below.
  identifyUser(trimmedUserId, email, displayName, phoneNumber, metadata);

  lastIdentifiedWebSignupUserId = trimmedUserId;
  // Attempt finished identifying; next attempt should open its own session.
  hasStartedSessionForCurrentAttempt = false;
};

interface PrepareWebSignupLogRocketSessionParams {
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

/**
 * Express checkout has no form-submit prospect step, so this starts a fresh
 * session and identifies it with the email before the backend create call. The
 * identify is what makes LogRocket start flushing data (forceRecord gates on it).
 * The save token is stashed at create and the replay is persisted after payment
 * succeeds on the download landing page via flushWebSignupLogRocketSave.
 */
export const prepareWebSignupLogRocketSession = ({
  email,
  name,
  firstName,
  lastName,
  phoneNumber,
}: PrepareWebSignupLogRocketSessionParams): void => {
  if (!isWebLogRecordingEnabled()) return;

  const trimmedEmail = email.trim();
  if (!trimmedEmail) return;

  startFreshLogRocketSession();
  hasStartedSessionForCurrentAttempt = true;
  lastIdentifiedWebSignupUserId = null;

  const displayName = buildSignupDisplayName(name, email, firstName, lastName);

  identifyUser(trimmedEmail, trimmedEmail, displayName, phoneNumber, {
    ...getWebSignupLogRocketMetadata(),
    signupStatus: "in-progress",
  });
};

interface SaveWebSignupLogRocketSessionAfterCreateParams {
  userId: string;
  token?: string | null;
}

interface PendingWebSignupLogRocketSave {
  userId: string;
  token: string;
}

/**
 * Stores the userId + save token issued by the backend create call so the replay
 * can be persisted AFTER payment finishes. We don't capture/save here because
 * create is immediately followed by Stripe's confirmPayment redirect, which tears
 * down the page (and any in-flight capture loop). Persisting to sessionStorage
 * lets the save survive the redirect and run from the stable landing page.
 */
export const stashWebSignupLogRocketSave = ({
  userId,
  token,
}: SaveWebSignupLogRocketSessionAfterCreateParams): void => {
  if (!isWebLogRecordingEnabled()) return;

  const trimmedUserId = userId.trim();
  if (!trimmedUserId || !token) return;

  try {
    const pending: PendingWebSignupLogRocketSave = {
      userId: trimmedUserId,
      token,
    };
    sessionStorage.setItem(
      WEB_LOG_RECORD_PENDING_SAVE_KEY,
      JSON.stringify(pending),
    );
  } catch {
    // ignore storage failures
  }
};

const readPendingWebSignupLogRocketSave =
  (): PendingWebSignupLogRocketSave | null => {
    try {
      const raw = sessionStorage.getItem(WEB_LOG_RECORD_PENDING_SAVE_KEY);
      if (!raw) return null;
      const pending = JSON.parse(raw) as PendingWebSignupLogRocketSave;
      if (!pending?.userId || !pending?.token) return null;
      return pending;
    } catch {
      return null;
    }
  };

const clearPendingWebSignupLogRocketSave = (): void => {
  try {
    sessionStorage.removeItem(WEB_LOG_RECORD_PENDING_SAVE_KEY);
  } catch {
    // ignore storage failures
  }
};

/**
 * Persists the sign-up replay using the stashed userId + token. Retries until
 * LogRocket exposes the session URL (no fixed-timeout race) and posts it to the
 * public token-secured endpoint. The stashed token is cleared only after the
 * save succeeds, so if LogRocket never exposes a URL within the capture retry
 * budget the token survives for a refresh/retry (backend token TTL is 30 min).
 * Fire-and-forget: never blocks or disrupts the signup/payment flow.
 */
const persistWebSignupLogRocketSession = ({
  userId,
  token,
}: PendingWebSignupLogRocketSave): void => {
  captureLogRocketSessionUrlWithRetry(
    (sessionUrl) => {
      const sessionId = sessionUrl.split("/").pop() || `session-${Date.now()}`;

      void saveWebSignupLogRocketSession({
        userId,
        sessionId,
        sessionUrl,
        startTime: Date.now(),
        platform: "web-signup",
        token,
      })
        .then(() => {
          clearPendingWebSignupLogRocketSave();
        })
        .catch(() => {
          // best-effort; keep the stashed token so a refresh/retry can re-attempt
        })
        .finally(() => {
          isFlushingWebSignupLogRocketSave = false;
        });
    },
    {
      // Capture skipped or never returned a URL within the retry budget: release
      // the guard so a later flush (re-render/refresh) can retry. The stashed
      // token is intentionally kept (backend token TTL is 30 min).
      onExhausted: () => {
        isFlushingWebSignupLogRocketSave = false;
      },
    },
  );
};

/**
 * Reads the stashed save then persists the replay. Called from the post-payment
 * landing page where the LogRocket session continues across the redirect and the
 * page is no longer being torn down by Stripe. The stashed token is intentionally
 * NOT cleared up front — it is cleared only after a successful save (see
 * persistWebSignupLogRocketSession). The in-flight guard prevents duplicate retry
 * loops from re-renders calling this multiple times.
 */
export const flushWebSignupLogRocketSave = (): void => {
  if (!isWebLogRecordingEnabled()) return;
  if (isFlushingWebSignupLogRocketSave) return;

  const pending = readPendingWebSignupLogRocketSave();
  if (!pending) return;

  isFlushingWebSignupLogRocketSave = true;
  persistWebSignupLogRocketSession(pending);
};
